import { useState } from 'react';
import { useBlocker } from 'react-router-dom';

/* Context */
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

/* API */
import { api } from '../api';

/* CSS Modules */
import styles from './HelpPage.module.css';
import shared from '../styles/shared.module.css';

/* List of Frequently Asked Questions (FAQs) */
import { FAQ_DATA } from '../data/faqData';

/* Icons */
import phone_icon from '../assets/icons/phone_icon.svg';
import email_icon from '../assets/icons/email_icon.svg';
import success_icon from '../assets/icons/success_icon.svg'

/* Confirmation Card */
import ConfirmationModal from '../components/cards/ConfirmationModal';

const HelpPage = () => {

    const { currentUser } = useAuthContext();
    const { showToast } = useToast();

    const [activeTab, setActiveTab] = useState('faqs');
    const [openFaqId, setOpenFaqId] = useState(null);

    const [formData, setFormData] = useState({
        subject: '',
        message: '',
    });
    const [attachmentFile, setAttachmentFile] = useState(null);

    /* State to track unsaved changes */
    const [isDirty, setIsDirty] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    /* Watches for navigation attempts (isDirty is true = blocks attempt) */
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isDirty && currentLocation.pathname !== nextLocation.pathname
    )

    const cancelNavigation = () => {
        if (blocker.state === 'blocked') blocker.reset();
    }

    const confirmNavigation = () => {
        if (blocker.state === 'blocked') blocker.proceed();
    }

    const toggleFaq = (id) => {
        setOpenFaqId(prev => prev === id ? null : id);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
    };

    const handleAttachmentChange = (e) => {
        setAttachmentFile(e.target.files[0] || null);
        setIsDirty(true);
    };

    const handleDismiss = () => {
        setShowSuccessModal(false);
        setFormData({ subject: '', message: '' });
        setAttachmentFile(null);
        setIsDirty(false);
    }

    const handleSend = async () => {
        if (!formData.subject.trim() || !formData.message.trim()) {
            showToast('Please fill in all required fields (Subject, Message).', 'error');
            return;
        }

        try {
            let attachment_url = null;

            if (attachmentFile) {
                const uploadData = new FormData();
                uploadData.append('image', attachmentFile);
                const uploadResult = await api.upload('/images', uploadData);
                attachment_url = uploadResult.url;
            }

            await api.post('/enquiries', {
                subject: formData.subject,
                message: formData.message,
                attachment_url
            });

            setIsDirty(false);
            setShowSuccessModal(true);
        } catch (error) {
            console.error("Error sending message:", error);
            showToast(error.message || 'Failed to send message. Please try again later.', 'error');
        }
    }

    return (
        <div>
            {/* Page header */}
            <section className={shared.heroHeader}>
                <div>
                    <h1 className={shared.heroTitle}>Help</h1>
                    <p className={shared.heroSubtitle}>Need help with something?</p>
                </div>
            </section>

            {/* Tab Switcher */}
            <div className={styles.helpTabs}>
                <button
                    className={`${styles.helpTab} ${activeTab === 'faqs' ? styles.active : ''}`}
                    onClick={() => setActiveTab('faqs')}
                >
                    FAQs
                </button>

                <button
                    className={`${styles.helpTab} ${activeTab === 'enquiry' ? styles.active : ''}`}
                    onClick={() => setActiveTab('enquiry')}
                >
                    Enquiry
                </button>
            </div>

            {/* Content Area */}
            <div className={styles.helpContent}>

                {/* FAQ */}
                {activeTab === 'faqs' && (
                    <div className={`${styles.helpContainer} ${styles.tabContainerAnimate}`}>
                        {FAQ_DATA.map((item) => {
                            const isOpen = openFaqId === item.id;

                            return (
                                <div
                                    key={item.id}
                                    className={`${styles.faqItem} ${isOpen ? styles.open : ''}`}
                                    onClick={() => toggleFaq(item.id)}
                                >
                                    <div className={styles.faqQuestionRow}>
                                        <div className={styles.faqIconWrapper}>
                                            <span className={styles.faqToggleSign}></span>
                                        </div>
                                        <div className={styles.faqQuestion}>{item.question}</div>
                                    </div>

                                    <div className={styles.faqAnswerWrapper}>
                                        <div className={styles.faqAnswer}>
                                            {typeof item.answer === 'function'
                                                ? item.answer({ onEnquiryClick: () => setActiveTab('enquiry') })
                                                : item.answer
                                            }
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Enquiry */}
                {activeTab === 'enquiry' && (
                    <div className={`${styles.helpContainer} ${styles.tabContainerAnimate}`}>
                        <div className={styles.enquiryWrapper}>
                            {/* Sidebar */}
                            <div className={styles.enquirySidebar}>
                                <div className={styles.enquirySidebarTitle}>Contact Information</div>
                                <div className={styles.enquirySidebarSubtitle}>Fill up the form and our team will get back to you within 24 hours.</div>

                                <div className={styles.enquirySidebarContact}>
                                    <img src={phone_icon} alt='Phone Number' className={styles.enquirySidebarPhone} />
                                    +61 402 420 062
                                </div>

                                <div className={styles.enquirySidebarContact}>
                                    <img src={email_icon} alt='Email Address' className={styles.enquirySidebarEmail} />
                                    info@thetravelbuddy.com.au
                                </div>
                            </div>

                            {/* Form */}
                            <div className={styles.enquiryForm}>

                                {/* Submitting as */}
                                <p className={styles.enquirySubmittingAs}>
                                    Submitting as <strong>{currentUser?.username}</strong> ({currentUser?.email})
                                </p>

                                {/* Subject */}
                                <div className={styles.enquiryFormGroup}>
                                    <label>Subject *</label>
                                    <input
                                        type='text'
                                        name='subject'
                                        className={styles.enquiryInput}
                                        placeholder='e.g. Bug report, Feature request...'
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                {/* Message */}
                                <div className={styles.enquiryFormGroup}>
                                    <label>Message *</label>
                                    <textarea
                                        name='message'
                                        className={`${styles.enquiryInput} ${styles.enquiryTextarea}`}
                                        placeholder='Write your message here...'
                                        value={formData.message}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                {/* Attachment */}
                                <div className={styles.enquiryFormGroup}>
                                    <label>Attachment (optional)</label>
                                    <input
                                        type='file'
                                        name='attachment'
                                        accept='image/png, image/jpeg, image/gif, image/webp, .pdf, .doc, .docx, .txt'
                                        className={styles.enquiryFileInput}
                                        onChange={handleAttachmentChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            type='button'
                            className={styles.enquirySendMessageButton}
                            title='Send Enquiry Message'
                            onClick={handleSend}
                        >
                            Send Message
                        </button>
                    </div>
                )}
            </div>

            {/* --- MODAL 1: UNSAVED CHANGES --- */}
            <ConfirmationModal
                isOpen={blocker.state === 'blocked'}
                title="Unsent Message"
                message="Are you sure you want to leave without sending your message?"
                onClose={cancelNavigation}
                onConfirm={confirmNavigation}
                confirmText="Leave"
                cancelText="Stay"
                isDanger={true}
            />

            {/* --- MODAL 2: SUCCESS --- */}
            <ConfirmationModal
                isOpen={showSuccessModal}
                title="Thank you"
                message="Your message has been received!"
                confirmText="Dismiss"
                onConfirm={handleDismiss}
                showCancel={false}
                customIcon={<img src={success_icon} alt="Success" />}
            />
        </div>
    );
};

export default HelpPage;
